import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Constants } from "@constants";
import {
    CanjePremioDTO,
    CanjeResultadoDTO,
    ICanjePremioDTO,
    ICanjeResultadoDTO,
    IPaginadoDTO,
    PaginadoDTO
} from "@DTOS";
import { Nilable } from "@shared/utils";
import { firstValueFrom } from "rxjs";

@Injectable({
    providedIn: "root"
})
export class CanjesServiceBackend {
    constructor(protected httpClient: HttpClient) {}

    public async getCanjesQuery(
        pagina: number, cantidad: number, email: Nilable<string> = null, type: Nilable<string> = null,
        state: Nilable<string> = null, pais: Nilable<string> = null
    ): Promise<PaginadoDTO<CanjePremioDTO>> {
        const response = await firstValueFrom(
            this.httpClient.get<IPaginadoDTO<ICanjePremioDTO>>(`${Constants.apiURL}/prize-exchanges`,
                {
                    params: Object.entries({
                        page: pagina,
                        size: cantidad,
                        q: email,
                        prize__type: type,
                        state,
                        user_country: pais
                    }).reduce(
                        (a, [k, v]) => {
                            if (v == null) {
                                return a;
                            } else {
                                a[k] = v;
                                return a;
                            }
                        }, {}
                    )
                }
            )
        );

        const canjesPremios = response?.results.map(canjePremio => new CanjePremioDTO(canjePremio)) ?? [];
        return new PaginadoDTO<CanjePremioDTO>({...response, results: canjesPremios});
    }

    public async getCantGiftcardsPendientesPorPrecio(prize_id: number): Promise<PaginadoDTO<CanjePremioDTO>> {
        const response = await firstValueFrom(
            this.httpClient.get<IPaginadoDTO<ICanjePremioDTO>>(`${Constants.apiURL}/prize-exchanges`,
                {
                    params: Object.entries({
                        state: "REQUESTED",
                        type: "GIFTCARD",
                        prize: prize_id
                    }).reduce(
                        (a, [k, v]) => {
                            if (v == null) {
                                return a;
                            } else {
                                a[k] = v;
                                return a;
                            }
                        }, {}
                    )
                }
            )
        );

        const canjesPremios = response.results.map(canjePremio => new CanjePremioDTO(canjePremio));
        return new PaginadoDTO<CanjePremioDTO>({...response, results: canjesPremios});
    }

    public async getCanjesEntregadosQuery(
        email: string, pagina?: number, cantidad?: number
    ): Promise<PaginadoDTO<CanjePremioDTO>> {
        const response = await firstValueFrom(
            this.httpClient.get<IPaginadoDTO<ICanjePremioDTO>>(
                `${Constants.apiURL}/prize-exchanges/exchange`,
                {
                    params: Object.entries({
                        pagina: (pagina && pagina.toString()) || null,
                        cantidad:(cantidad && cantidad.toString()) || null,
                        email
                    }).reduce(
                        (a, [k, v]) => {
                            if (v == null) {
                                return a;
                            } else {
                                a[k] = v;
                                return a;
                            }
                        }, {}
                    )
                }
            )
        );

        const canjesPremios = response.results.map(canjePremio => new CanjePremioDTO(canjePremio));
        return new PaginadoDTO<CanjePremioDTO>({...response, results: canjesPremios});
    }

    public async patchCanje(id: number, data: any = {}): Promise<CanjePremioDTO> {
        const response = await firstValueFrom(
            this.httpClient.patch<ICanjePremioDTO>(
                `${Constants.apiURL}/prize-exchanges/${id}`, data
            )
        );

        return new CanjePremioDTO(response);
    }

    public async getCanje(id: number): Promise<CanjePremioDTO> {
        const response = await firstValueFrom(
            this.httpClient.get<ICanjePremioDTO>(
                `${Constants.apiURL}/prize-exchanges/${id}`
            )
        );

        return new CanjePremioDTO(response);
    }

    public async getDtoneBalance(): Promise<any> {
        return await firstValueFrom(
            this.httpClient.get<number>(`${Constants.apiURL}/dtone_balance`)
        );
    }

    public async reembolsarCanje(canje: CanjePremioDTO): Promise<CanjePremioDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<ICanjePremioDTO>(
                `${Constants.apiURL}/prize-exchanges/${canje.id}/refund`, {}
            )
        );

        return new CanjePremioDTO(response);
    }


    public async entregarCanje(canje: CanjePremioDTO, data: any = {}): Promise<Nilable<CanjeResultadoDTO>> {
        const response = await firstValueFrom(
            this.httpClient.post<ICanjeResultadoDTO>(
                `${Constants.apiURL}/prize-exchanges/${canje.id}/deliver`, data
            )
        );

        return response == null ? null : new CanjeResultadoDTO(response);
    }

    public async autorizarCanje(canje: CanjePremioDTO): Promise<CanjeResultadoDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<ICanjeResultadoDTO>(
                `${Constants.apiURL}/prize-exchanges/${canje.id}/authorize`, {}
            )
        );

        return new CanjeResultadoDTO(response);
    }

    public async recuperarUser(canje: CanjePremioDTO): Promise<any> {
        return firstValueFrom(this.httpClient.post<any>(
            `${Constants.apiURL}/prize-exchanges/${canje.id}/recover_user`, {}
        )).then(res => res).catch(error => error);
    }

    public async enviarCanjes(): Promise<string> {
        const response = await firstValueFrom(
            this.httpClient.post(
                `${Constants.apiURL}/prize-exchanges/deliver_all`, null
            )
        );

        return JSON.stringify(response);
    }
}
